import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Context } from '../../../../context';
import { buildPutWidgetConfigPayload } from '../buildPutWidgetConfigPayload';
import { buildPatientDashboardLayoutSignature } from '../widgetLayoutDraft';
import {
  normalizeApiWidget,
  renumberPatientDashboardWidgetMenuOrder,
} from '../normalizeWidgetConfig';

/** Wait after last add/remove before sending PUT (optimistic UI stays immediate). */
const SAVE_DEBOUNCE_MS = 1500;
/** Backoff before retrying after a failed PUT (HTTP error or network). */
const SAVE_RETRY_MS = 5000;
/** Failed PUT attempts retried after the initial send (then notify and stop). */
const SAVE_MAX_RETRIES = 2;

type UsePersistPatientDashboardWidgetLayoutArgs = {
  enabled: boolean;
  dashboard: mumms.WidgetConfigDashboard;
  mergedDashboardConfig: mumms.WidgetConfig[];
  hasSessionLayoutEdits: boolean;
  onPersistSuccess: () => void;
  /** Called after {@link SAVE_MAX_RETRIES} failed retries for the current layout. */
  onPersistRetriesExhausted?: () => void;
};

/**
 * Debounced PUT of patient dashboard USER layout after session add/remove.
 *
 * **Optimistic UI:** While dirty, {@link mergedDashboardConfig} (grid + session
 * deltas) is what the user sees. The server is reconciled only after a
 * **non-stale** PUT succeeds.
 *
 * **Stale PUT handling:** Each save captures `signatureAtSend`. On response,
 * reconcile (`onPatientDashboardLayoutSaved`, clear session deltas) only when
 * `signatureAtSend === latestLayoutSignatureRef.current`. Otherwise the
 * response is ignored and the debounce effect schedules a save for the newer
 * layout. Overlapping PUTs are allowed; only the matching response applies.
 *
 * **Background GET:** {@link setWidgetConfigLayoutDirty} blocks root
 * {@link refreshWidgetConfigInBackground} while session edits are dirty; a
 * deferred refresh runs after save clears dirty state.
 *
 * Failed PUTs retry up to {@link SAVE_MAX_RETRIES} times (5s apart), then
 * {@link onPersistRetriesExhausted} runs. A new layout edit resets the retry
 * budget and schedules a fresh debounced save.
 *
 * No-op when palette is disabled (env fallback) or config-service is unavailable.
 */
export function usePersistPatientDashboardWidgetLayout({
  enabled,
  dashboard,
  mergedDashboardConfig,
  hasSessionLayoutEdits,
  onPersistSuccess,
  onPersistRetriesExhausted,
}: UsePersistPatientDashboardWidgetLayoutArgs): void {
  /** Config-service client, user id, and callbacks from root Dashboard context. */
  const {
    serviceConfig,
    configUserId,
    agency,
    patient,
    onDashboardLayoutSaved,
    setWidgetConfigLayoutDirty,
  } = useContext(Context);

  const patientNumber = patient?.patientNumber;

  /**
   * Bumps on failed saves to re-run the debounce effect and retry the same
   * layout signature after {@link SAVE_RETRY_MS}.
   */
  const [saveRetryTick, setSaveRetryTick] = useState(0);
  /** Failed PUT count for the current {@link layoutSignature} (reset on edit/success). */
  const saveRetryCountRef = useRef(0);

  /**
   * Signature of the layout the user has *right now* (updated every render).
   * Compared against `signatureAtSend` when a PUT completes.
   */
  const latestLayoutSignatureRef = useRef('');

  /** Memoized signature for effect deps — when this changes, debounce resets. */
  const layoutSignature = useMemo(
    () => buildPatientDashboardLayoutSignature(mergedDashboardConfig),
    [mergedDashboardConfig],
  );

  /** Keep ref in sync so in-flight PUT handlers see post-edit signatures. */
  latestLayoutSignatureRef.current = layoutSignature;

  /** New layout edit — fresh debounced save with full retry budget. */
  useEffect(() => {
    saveRetryCountRef.current = 0;
  }, [layoutSignature]);

  const scheduleSaveRetry = useCallback(() => {
    if (saveRetryCountRef.current >= SAVE_MAX_RETRIES) {
      console.warn(
        '[widget-config] layout save failed; retries exhausted',
        SAVE_MAX_RETRIES,
      );
      if (onPersistRetriesExhausted) {
        onPersistRetriesExhausted();
      }
      return;
    }

    saveRetryCountRef.current += 1;
    console.warn(
      '[widget-config] layout save failed; will retry',
      saveRetryCountRef.current,
      'of',
      SAVE_MAX_RETRIES,
    );
    window.setTimeout(
      () => setSaveRetryTick((tick) => tick + 1),
      SAVE_RETRY_MS,
    );
  }, [onPersistRetriesExhausted]);

  /**
   * Build payload from current merged layout, PUT to config-service, reconcile
   * on success only if the response is still fresh.
   */
  const persistLayout = useCallback(async () => {
    /** Guard: env fallback, missing client/user, or nothing to persist. */
    if (!enabled || !configUserId || !serviceConfig || !hasSessionLayoutEdits) {
      return;
    }

    /** Fingerprint of the body we are about to send. */
    const signatureAtSend = buildPatientDashboardLayoutSignature(
      mergedDashboardConfig,
    );

    const layoutForSave = renumberPatientDashboardWidgetMenuOrder(
      mergedDashboardConfig.map((w) => ({ ...w })),
    );
    const payload = buildPutWidgetConfigPayload(
      layoutForSave,
      configUserId,
      dashboard,
    );

    try {
      const response = await serviceConfig.saveWidgetConfig(payload, {
        dashboard,
        ...(patientNumber ? { patientNumber } : {}),
        ...(agency ? { agency } : {}),
      });

      if (response instanceof Response) {
        scheduleSaveRetry();
        return;
      }

      /**
       * Stale response: user edited again while this PUT was in flight.
       * Optimistic UI + session deltas stay authoritative; do not merge this
       * response or clear dirty state. A newer debounced save is already
       * scheduled when {@link layoutSignature} changed.
       */
      if (signatureAtSend !== latestLayoutSignatureRef.current) {
        console.warn(
          '[widget-config] ignoring stale layout save response',
          signatureAtSend,
          latestLayoutSignatureRef.current,
        );
        return;
      }

      const savedLayout = response.map((row: mumms.WidgetConfigApiItem) =>
        normalizeApiWidget(row),
      );

      /** Fresh success: server layout becomes baseline; session deltas cleared. */
      saveRetryCountRef.current = 0;
      if (onDashboardLayoutSaved) {
        onDashboardLayoutSaved(savedLayout, dashboard);
      }
      onPersistSuccess();
      if (setWidgetConfigLayoutDirty) {
        setWidgetConfigLayoutDirty(false);
      }
    } catch (error) {
      console.warn('[widget-config] layout save error', error);
      scheduleSaveRetry();
    }
  }, [
    enabled,
    configUserId,
    serviceConfig,
    hasSessionLayoutEdits,
    mergedDashboardConfig,
    onDashboardLayoutSaved,
    dashboard,
    patientNumber,
    agency,
    onPersistSuccess,
    setWidgetConfigLayoutDirty,
    scheduleSaveRetry,
  ]);

  /**
   * Debounce saves on layout changes and failed-save retries.
   * Cleanup clears the pending timer when the user edits again (new signature).
   */
  useEffect(() => {
    /** No session edits or API path disabled — not dirty. */
    if (!enabled || !hasSessionLayoutEdits) {
      if (setWidgetConfigLayoutDirty) {
        setWidgetConfigLayoutDirty(false);
      }
      return undefined;
    }

    /** Block background GET refresh while unsaved edits exist. */
    if (setWidgetConfigLayoutDirty) {
      setWidgetConfigLayoutDirty(true);
    }

    const timer = window.setTimeout(() => {
      persistLayout().catch(() => undefined);
    }, SAVE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [
    enabled,
    hasSessionLayoutEdits,
    layoutSignature,
    saveRetryTick,
    persistLayout,
    setWidgetConfigLayoutDirty,
  ]);
}
